import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Clock, MessageSquare, UserCheck, Send, ThumbsUp, AtSign, FolderPlus, UserPlus } from 'lucide-react';
import { useChAuth } from '../contexts/ChAuthContext';
import { chNotificationService, resolveNotificationTarget } from '../services/notificationService';
import { useModal } from '../../contexts/ModalContext';
import type { ChNotification, ChNotificationType } from '../types';

function getRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Agora mesmo';
  if (diffMin < 60) return `${diffMin}min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays < 7) return `${diffDays}d atrás`;
  return date.toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' });
}

function getNotificationIcon(type: ChNotificationType) {
  switch (type) {
    case 'client_approved': return ThumbsUp;
    case 'approved': return UserCheck;
    case 'changes_requested': return Send;
    case 'commented': return MessageSquare;
    case 'assigned': return UserPlus;
    case 'mentioned': return AtSign;
    case 'added_to_project': return FolderPlus;
    default: return Bell;
  }
}

function getNotificationColor(type: ChNotificationType): string {
  switch (type) {
    case 'client_approved': return 'bg-emerald-100 text-emerald-700';
    case 'approved': return 'bg-blue-100 text-blue-700';
    case 'changes_requested': return 'bg-amber-100 text-amber-700';
    case 'commented': return 'bg-purple-100 text-purple-700';
    case 'assigned': return 'bg-cyan-100 text-cyan-700';
    case 'mentioned': return 'bg-pink-100 text-pink-700';
    case 'added_to_project': return 'bg-lime-100 text-lime-800';
    default: return 'bg-gray-100 text-gray-700';
  }
}

export function ChNotificationsPage() {
  const { user, accessInfo } = useChAuth();
  const navigate = useNavigate();
  const { toast } = useModal();
  const [notifications, setNotifications] = useState<ChNotification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    loadNotifications();

    const unsubscribe = chNotificationService.subscribeToNew(user.id, (newNotif) => {
      setNotifications(prev => [newNotif, ...prev]);
    });

    return unsubscribe;
  }, [user]);

  async function loadNotifications() {
    if (!user) return;
    try {
      const data = await chNotificationService.getByUser(user.id);
      setNotifications(data);
    } finally {
      setLoading(false);
    }
  }

  async function handleClick(notif: ChNotification) {
    if (!notif.read) {
      await chNotificationService.markAsRead(notif.id);
      setNotifications(prev =>
        prev.map(n => n.id === notif.id ? { ...n, read: true } : n)
      );
    }
    if (!notif.post_id && !notif.project_id) return;
    const target = await resolveNotificationTarget(notif, accessInfo.isAgencyStaff).catch(() => null);
    if (target) {
      navigate(target);
    } else {
      toast.info('Este conteúdo já não está disponível');
    }
  }

  async function handleMarkAllRead() {
    if (!user) return;
    await chNotificationService.markAllAsRead(user.id);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="p-4 md:p-8 ch-animate-in">
        <h1 className="ch-heading ch-heading-lg mb-6">Notificações</h1>
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 ch-animate-in">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="ch-heading ch-heading-lg">Notificações</h1>
          {unreadCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--ch-magenta)] text-white text-xs font-bold">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="ch-btn ch-btn-secondary text-sm flex items-center gap-2"
          >
            <CheckCheck className="w-4 h-4" />
            Marcar todas como lidas
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="ch-card">
          <div className="ch-empty">
            <Bell className="ch-empty-icon" />
            <p className="text-gray-500 font-medium">Sem notificações</p>
            <p className="text-sm text-gray-400 mt-1">
              As notificações aparecerão aqui quando houver atividade nos teus projetos.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map(notif => {
            const Icon = getNotificationIcon(notif.type);
            const colorClass = getNotificationColor(notif.type);

            return (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 hover:shadow-sm ${
                  notif.read
                    ? 'bg-white border-gray-100 opacity-70'
                    : 'bg-white border-gray-200 shadow-sm'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm leading-relaxed ${notif.read ? 'text-gray-500' : 'text-[var(--ch-ink)] font-medium'}`}>
                      {notif.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Clock className="w-3 h-3 text-gray-300" />
                      <span className="text-xs text-gray-400">
                        {getRelativeTime(notif.created_at)}
                      </span>
                    </div>
                  </div>
                  {!notif.read && (
                    <div className="w-2.5 h-2.5 rounded-full bg-[var(--ch-magenta)] shrink-0 mt-1.5" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
